import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { DualAxes } from '@ant-design/plots';
import initialChartConfig from '../chartConfig/initialChartConfig';

const DualAxesViz = (props) => {
  const {
    formData,
    chartData,
    chartConfig,
    chartsFromDashboard,
  } = props;
  const chartOptions = useSelector((state: any) => state.chartsMetaData?.chartOptions);
  const chartConfigData = useSelector((state: any) => state.chartsMetaData?.chartConfig);
  const configType = 'dualAxes';

  let finalChartConfigData = chartConfigData || initialChartConfig(configType, formData);

  if (chartsFromDashboard && chartConfig && configType in chartConfig) {
    finalChartConfigData = chartConfig;
  }

  const { x_axis_label, y1_axis_label, y2_axis_label } = formData ?? {};

  const mergeChartData = () => {
    if (!Array.isArray(chartData) || !x_axis_label || !y1_axis_label || !y2_axis_label) {
      return [[], []];
    }
    const mergedMap = new Map<string, Record<string, any>>();
    chartData.forEach((item) => {
      const xVal = item.rawData ? item.rawData[x_axis_label] : item['x'];
      const y1Val = item.rawData ? item.rawData[y1_axis_label] : item['y1'];
      const y2Val = item.rawData ? item.rawData[y2_axis_label] : item['y2'];
      if (xVal === null || xVal === undefined) return;
      if (!mergedMap.has(xVal)) {
        mergedMap.set(xVal, { [x_axis_label]: xVal });
      }
      const record = mergedMap.get(xVal)!;
      if (y1Val !== undefined) record[y1_axis_label] = y1Val;
      if (y2Val !== undefined) record[y2_axis_label] = y2Val;
    });
    const mergedArray = Array.from(mergedMap.values());
    return [mergedArray, mergedArray];
  };

  const filteredData =
    (!chartData.data || !chartData.data.length) ? mergeChartData() : [chartData.data, chartData.data];

  const tickLineConfig = {
    length: 4,
    style: {
      stroke: '#d9d9d9',
      lineWidth: 1,
    },
  };

  const rulersConfig = {
    style: {
      stroke: '#ccc',
      lineWidth: 1,
    },
  };

  const gridStyle = {
    line: {
      style: {
        stroke: '#e0e0e0',
        lineDash: [4, 4],
      },
    },
  };

  const y1ChartType =
    chartOptions?.y1Axis?.y1AxisChartType?.data || formData?.y1_axis_chart_type || 'line';
  const y2ChartType =
    chartOptions?.y2Axis?.y2AxisChartType?.data || formData?.y2_axis_chart_type || 'line';

  const geometryOptions: any = [
    {
      geometry: y1ChartType,
      color: finalChartConfigData?.graphic?.color?.data?.[0],
      tooltip: finalChartConfigData?.graphic?.tooltip?.data,
      lineStyle: {
        lineWidth: finalChartConfigData?.graphic?.columnSize?.thicknessValue?.data,
      },
    },
    {
      geometry: y2ChartType,
      color: finalChartConfigData?.graphic?.color?.data?.[1],
      tooltip: finalChartConfigData?.graphic?.tooltip?.data,
      lineStyle: {
        lineWidth: finalChartConfigData?.graphic?.columnSize?.thicknessValue?.data,
      },
    },
  ];

  const yAxisConfig: any = {};

  if (finalChartConfigData?.dualAxes?.y1Axis?.data) {
    yAxisConfig[y1_axis_label] = {
      title: finalChartConfigData?.y1Axis?.y1Title?.data
        ? { text: finalChartConfigData?.y1Title?.text?.data || y1_axis_label }
        : false,
      min: finalChartConfigData?.y1Axis?.minValue?.data,
      max: finalChartConfigData?.y1Axis?.maxValue?.data,
      tickLine: finalChartConfigData?.y1Axis?.tickLine?.data ? tickLineConfig : false,
      line: finalChartConfigData?.y1Axis?.axisRulers?.data ? rulersConfig : false,
      grid: finalChartConfigData?.y1Axis?.grid?.data ? gridStyle : null,
      position: 'left',
    };
  } else {
    yAxisConfig[y1_axis_label] = {};
  }

  if (finalChartConfigData?.dualAxes?.y2Axis?.data) {
    yAxisConfig[y2_axis_label] = {
      title: finalChartConfigData?.y2Axis?.y2Title?.data
        ? { text: finalChartConfigData?.y2Title?.text?.data || y2_axis_label }
        : false,
      min: finalChartConfigData?.y2Axis?.minValue?.data,
      max: finalChartConfigData?.y2Axis?.maxValue?.data,
      tickLine: finalChartConfigData?.y2Axis?.tickLine?.data ? tickLineConfig : false,
      line: finalChartConfigData?.y2Axis?.axisRulers?.data ? rulersConfig : false,
      grid: finalChartConfigData?.y2Axis?.grid?.data ? gridStyle : null,
      position: 'right',
    };
  } else {
    yAxisConfig[y2_axis_label] = {};
  }

  const config: any = {
    data: filteredData,
    xField: x_axis_label,
    yField: [y1_axis_label, y2_axis_label],
    geometryOptions,
    meta: {
      [y1_axis_label]: {
        alias: finalChartConfigData?.dualAxes?.y1Axis?.data  && finalChartConfigData?.y1Axis?.y1Title?.data
          ? finalChartConfigData?.y1Title?.text?.data
          : y1_axis_label,
      },
      [y2_axis_label]: {
        alias: finalChartConfigData?.dualAxes?.y2Axis?.data  && finalChartConfigData?.y2Axis?.y2Title?.data
          ? finalChartConfigData?.y2Title?.text?.data
          : y2_axis_label,
      },
    },
    xAxis: finalChartConfigData?.dualAxes?.xAxis?.data ?{
      title: finalChartConfigData?.xAxis?.xTitle?.data
        ? { text: finalChartConfigData?.xTitle?.text?.data || x_axis_label }
        : false,
      tickLine: finalChartConfigData?.xAxis?.tickLine?.data ? tickLineConfig : false,
      line: finalChartConfigData?.xAxis?.axisRulers?.data ? rulersConfig : false,
      grid: finalChartConfigData?.xAxis?.grid?.data ? gridStyle : null,
    }: {},
    yAxis: yAxisConfig,
    legend: {
      position: finalChartConfigData?.legend?.location?.data || 'top',
    },
  };

  return <DualAxes {...config} />;
};

const areEqual = (prevProps, nextProps) =>
  prevProps.formData === nextProps.formData &&
  prevProps.chartData === nextProps.chartData &&
  prevProps.chartConfig === nextProps.chartConfig &&
  prevProps.chartsFromDashboard === nextProps.chartsFromDashboard;

export default memo(DualAxesViz, areEqual);